import _ from 'lodash';
import type { ChartModel, ChartToolPanelsDef, ChartType, ColDef, GridApi } from 'ag-grid-community';
import { TREND_COLORS } from '@/trendData/trendData.constants';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { AG_GRID_GROUPING_COL_ID } from '@/tableBuilder/tableBuilder.constants';
import { formatNumber } from '@/utilities/numberHelper.utilities';
import { AgAxisLabelFormatterParams, AgChartTheme } from 'ag-charts-community';
import { AgGridRowIds, ColumnOrRowWithDefinitions } from '@/tableBuilder/tableBuilder.types';

const AG_GRID_DEFAULT_CATEGORY = 'AG-GRID-DEFAULT-CATEGORY';

const getCategoryValue = (rowIds: AgGridRowIds, key: string | number | { labels: string[] }) => {
  const value =
    typeof key === 'object' && key.labels?.length ? key.labels.find((label) => label.includes('_')) ?? key : key;

  const returnValue = typeof value === 'string' ? getFormattedLabel(rowIds, value) : key;

  return isNaN(Number(returnValue)) || !returnValue ? returnValue : parseFloat(Number(returnValue).toFixed(3));
};

const getCategoryKey = (datum: any, calloutKey?: string) => {
  const defaultReturn = _.values(datum)[0];
  /* Using the categoryId as key if (None) header is selected */
  if (_.has(datum, AG_GRID_DEFAULT_CATEGORY)) {
    return _.has(datum, `${AG_GRID_DEFAULT_CATEGORY}.id`)
      ? _.get(datum, `${AG_GRID_DEFAULT_CATEGORY}.id`)
      : _.get(datum, AG_GRID_DEFAULT_CATEGORY) - 1;
  }

  /* Grouped datum has slightly different structure
   *  All labels will have the same columnId, thus we look up the first one */
  if (_.has(datum, AG_GRID_GROUPING_COL_ID)) {
    const labels = _.get(datum, AG_GRID_GROUPING_COL_ID)?.labels ?? [];
    const label = labels.find((label: string) => label?.includes('_'));
    return label ? label.split('_')[0] : defaultReturn;
  }

  if (calloutKey && _.has(datum, calloutKey)) {
    const value = datum[calloutKey]?.value || datum[calloutKey];
    return typeof value === 'string' ? value.split('_').slice(0, -1).join('_') : value;
  }

  /* This handles the case where the selected category has an id (works like a safe-net before returning default) */
  const idKey = _.findKey(datum, (value) => _.has(value, 'id'));

  return idKey ? datum[idKey].id : defaultReturn;
};

const getTableCellValue = ({
  rowIds,
  agGridApi,
  datum,
  chartKey,
  calloutKey,
  defaultValue,
}: {
  rowIds: AgGridRowIds;
  agGridApi?: GridApi<any>;
  datum?: any;
  chartKey?: string;
  calloutKey?: string;
  defaultValue?: string;
}) => {
  const defaultCellValue = formatNumber(defaultValue)?.toString();
  const key = getCategoryKey(datum, calloutKey);
  const rowId =
    calloutKey && !datum[AG_GRID_DEFAULT_CATEGORY]
      ? key
      : _.findKey(rowIds, (rowId) => Object.values(rowId).includes(key));

  const rowNode = agGridApi?.getRowNode(rowId);

  if (!rowNode || !chartKey) {
    return defaultCellValue;
  }

  return (
    agGridApi?.getCellValue({
      rowNode,
      colKey: chartKey,
      useFormatter: true,
    }) ?? defaultCellValue
  );
};

const getFormattedLabel = (rowIds: AgGridRowIds, fullLabel?: string) => {
  if (typeof fullLabel === 'string') {
    // Check if any key from rowIds exists in fullLabel
    const keyToRemove = Object.keys(rowIds).find((key) => fullLabel.includes(key));
    if (keyToRemove) {
      return fullLabel.replace(`${keyToRemove}_`, '').trim();
    }
  }

  return fullLabel;
};

const createSeriesConfig = ({
  agGridApi,
  rowIds,
  isScatter = false,
}: {
  rowIds: AgGridRowIds;
  agGridApi?: GridApi<any>;
  isScatter?: boolean;
}) => ({
  tooltip: {
    renderer: (params: any) => {
      const value = getTableCellValue({
        rowIds,
        agGridApi,
        datum: params?.datum,
        chartKey: params?.yKey,
        calloutKey: isScatter ? params?.labelKey : params?.xKey,
        defaultValue: params?.value,
      });

      return {
        title: getFormattedLabel(rowIds, params.title) || '',
        data: [{ value, label: getFormattedLabel(rowIds, params.title)?.toString() ?? '' }],
      };
    },
  },
  label: {
    enabled: true,
    formatter: (params: any) =>
      getTableCellValue({
        rowIds,
        agGridApi,
        datum: params?.datum,
        chartKey: params?.yKey,
        calloutKey: isScatter ? params?.labelKey : params?.xKey,
        defaultValue: params?.value,
      }),
  },
});

const createPolarSeriesConfig = ({
  agGridApi,
  rowIds,
  isRadialBar = false,
}: {
  rowIds: AgGridRowIds;
  agGridApi?: GridApi<any>;
  isRadialBar?: boolean;
}) => ({
  label: {
    enabled: true,
    formatter: (params: any) => {
      const chartKey = isRadialBar ? params?.angleKey : params?.radiusKey;
      const calloutKey = isRadialBar ? params?.radiusKey : params?.angleKey;

      return getTableCellValue({
        rowIds,
        agGridApi,
        chartKey,
        calloutKey,
        datum: params?.datum,
        defaultValue: params?.value,
      });
    },
  },
  tooltip: {
    renderer: (params: any) => {
      const chartKey = isRadialBar ? params?.angleKey : params?.radiusKey;
      const calloutKey = isRadialBar ? params?.radiusKey : params?.angleKey;

      const value = getTableCellValue({
        rowIds,
        agGridApi,
        chartKey,
        calloutKey,
        datum: params?.datum,
        defaultValue: params?.title,
      });

      return {
        data: [{ value, label: getCategoryValue(rowIds, params.title)?.toString() ?? '' }],
      };
    },
  },
});

export const persistChart = (
  isInteractiveContent: boolean,
  api: GridApi<any> | undefined,
  setChartSettings: (settings?: ChartModel) => void,
) => {
  if (isInteractiveContent || headlessRenderMode()) {
    return;
  }

  setChartSettings(_.cloneDeep(_.first(api?.getChartModels())));
};

export const regenerateChart = (containerId: string, api: GridApi<any> | undefined, columnDefs: ColDef<any, any>[]) => {
  api?.createRangeChart({
    chartContainer: document.querySelector(containerId) as HTMLElement,
    cellRange: {
      columns: columnDefs.map((column) => column.colId) as string[],
    },
    chartType: 'stackedColumn',
  });
};

export const updateChartViewCellRange = (
  column: ColumnOrRowWithDefinitions,
  columns: ColumnOrRowWithDefinitions[],
  chartViewSettings: any,
  setChartViewSettings: (settings: any) => void,
  signalId?: string,
): void => {
  const isRemovingColumn = columns.some((col) => col.key === (signalId ? `${column.key}_${signalId}` : column.key));
  const normalizedColId = signalId ? `${column.key}_${signalId}` : column.key?.replace('.', '_');
  const currentColumns: string[] = _.cloneDeep(chartViewSettings?.cellRange?.columns ?? []);
  const shouldRemoveFromCellRange = currentColumns?.includes(normalizedColId) || isRemovingColumn;

  const newColumns = shouldRemoveFromCellRange
    ? currentColumns.filter((col) => col !== normalizedColId)
    : [...currentColumns, normalizedColId];

  setChartViewSettings({ ...chartViewSettings, cellRange: { ...chartViewSettings?.cellRange, columns: newColumns } });
};

export const SINGLE_SERIES_CHART_TYPES = ['pie', 'histogram'];

export const getSeeqAgChartTheme = ({
  agGridApi,
  rowIds,
  darkMode,
  chartViewColors = null,
}: {
  rowIds: AgGridRowIds;
  agGridApi?: GridApi<any>;
  darkMode?: boolean;
  chartViewColors?: string[] | null;
}) =>
  ({
    palette: {
      fills: chartViewColors ?? TREND_COLORS,
      strokes: chartViewColors ?? TREND_COLORS,
    },
    overrides: {
      'common': {
        background: {
          fill: darkMode ? '#242525' : 'white',
        },
        axes: {
          'category': {
            label: {
              color: darkMode ? '#C2BCB0' : '#242525',
              fontFamily: 'Source Sans Pro, Helvetica Nueu, Arial sans-serif',
              formatter: (params: AgAxisLabelFormatterParams) =>
                getCategoryValue(rowIds, params?.value?.value ?? params?.value)?.toString(),
            },
          },
          'grouped-category': {
            label: {
              color: darkMode ? '#C2BCB0' : '#242525',
              fontFamily: 'Source Sans Pro, Helvetica Nueu, Arial sans-serif',
              formatter: (params) => getCategoryValue(rowIds, params?.value?.value ?? params?.value)?.toString(),
            },
          },
          'number': {
            label: {
              color: darkMode ? '#C2BCB0' : '#242525',
              fontFamily: 'Source Sans Pro, Helvetica Nueu, Arial sans-serif',
            },
          },
          'angle-category': {
            label: {
              color: darkMode ? '#C2BCB0' : '#242525',
              fontFamily: 'Source Sans Pro, Helvetica Nueu, Arial sans-serif',
              formatter: (params) => getCategoryValue(rowIds, params?.value?.value ?? params?.value)?.toString(),
            },
          },
          'radius-category': {
            label: {
              color: darkMode ? '#C2BCB0' : '#242525',
              fontFamily: 'Source Sans Pro, Helvetica Nueu, Arial sans-serif',
              formatter: (params) => getCategoryValue(rowIds, params?.value?.value ?? params?.value)?.toString(),
            },
          },
        },
        padding: { right: 40 },
        title: {
          color: darkMode ? '#C2BCB0' : 'black',
          enabled: false,
          fontSize: 16,
          fontFamily: 'Source Sans Pro, Helvetica Nueu, Arial sans-serif',
          text: 'Title',
        },
        legend: {
          enabled: true,
          spacing: 30,
          item: {
            label: {
              fontSize: 12,
              fontFamily: 'Source Sans Pro, Helvetica Nueu, Arial sans-serif',
              color: darkMode ? '#C2BCB0' : 'black',
              formatter: (params) => getCategoryValue(rowIds, params?.value)?.toString(),
            },
          },
        },
      },
      'bar': {
        series: createSeriesConfig({ agGridApi, rowIds }),
      },
      'radar-line': {
        series: createPolarSeriesConfig({ agGridApi, rowIds }),
      },
      'radar-area': {
        series: createPolarSeriesConfig({ agGridApi, rowIds }),
      },
      'nightingale': { series: createPolarSeriesConfig({ agGridApi, rowIds }) },
      'radial-column': { series: createPolarSeriesConfig({ agGridApi, rowIds }) },
      'radial-bar': { series: createPolarSeriesConfig({ agGridApi, rowIds, isRadialBar: true }) },
      'line': {
        series: createSeriesConfig({ agGridApi, rowIds }),
      },
      'area': {
        series: createSeriesConfig({ agGridApi, rowIds }),
      },
      'scatter': {
        series: createSeriesConfig({ agGridApi, rowIds, isScatter: true }),
      },
      'bubble': {
        series: createSeriesConfig({ agGridApi, rowIds, isScatter: true }),
      },
      'pie': {
        series: {
          tooltip: {
            renderer: (params) => {
              const value = getTableCellValue({
                rowIds,
                agGridApi,
                datum: params?.datum,
                chartKey: params?.sectorLabelKey,
                calloutKey: params?.calloutLabelKey,
                defaultValue: params?.datum[params.sectorLabelKey!],
              });
              const title = getFormattedLabel(rowIds, params.title) || '';
              return {
                title,
                data: [{ value, label: title }],
              };
            },
          },
          sectorLabel: {
            enabled: true,
            formatter: (params) =>
              getTableCellValue({
                rowIds,
                agGridApi,
                datum: params?.datum,
                chartKey: params?.sectorLabelKey,
                calloutKey: params?.calloutLabelKey,
                defaultValue: params?.value,
              }),
          },

          calloutLabel: {
            enabled: true,
            formatter: (params) => getCategoryValue(rowIds, params?.value)?.toString(),
          },
        },
      },
      'donut': {
        series: {
          // @ts-ignore - HACK: AG Grid does not support angleNAme formatter for donut chart
          title: '',
          tooltip: {
            renderer: (params) => {
              const value = getTableCellValue({
                rowIds,
                agGridApi,
                datum: params?.datum,
                chartKey: params?.sectorLabelKey,
                calloutKey: params?.calloutLabelKey,
                defaultValue: params?.datum[params.sectorLabelKey!],
              });

              const title = getFormattedLabel(rowIds, params.title);
              return {
                title,
                data: [
                  {
                    value: `${getCategoryValue(rowIds, params.datum[params.calloutLabelKey!])}: ${value}`,
                    label: title || '',
                  },
                ],
              };
            },
          },
          sectorLabel: {
            enabled: true,
            formatter: (params) =>
              getTableCellValue({
                rowIds,
                agGridApi,
                datum: params?.datum,
                chartKey: params?.sectorLabelKey,
                calloutKey: params?.calloutLabelKey,
                defaultValue: params?.value,
              }),
          },
          calloutLabel: {
            enabled: true,
            formatter: (params) => getCategoryValue(rowIds, params.value)?.toString(),
          },
        },
      },
      'histogram': {
        series: {
          tooltip: {
            renderer: (params) => {
              const formattedTitle = `${params.xName}: ${params.datum.domain[0]} - ${params.datum.domain[1]}`;
              return {
                title: formattedTitle,
              };
            },
          },
        },
      },
    },
  } as AgChartTheme);

export const gridOptions = {
  chartThemeOverrides: {
    common: {
      animation: {
        enabled: false,
      },
      zoom: {
        enabled: false,
      },
      background: {
        fill: 'transparent',
      },
    },
  },
};

export const getChartToolPanelsDef = (isSimpleChart: boolean): ChartToolPanelsDef => ({
  defaultToolPanel: 'settings',
  settingsPanel: {
    /* Temporarily commenting out some chart types, to gradually integrate them */
    chartGroupsDef: {
      columnGroup: ['column', 'stackedColumn', 'normalizedColumn'],
      barGroup: ['bar', 'stackedBar', 'normalizedBar'],
      pieGroup: ['donut', 'pie'],
      scatterGroup: ['scatter', 'bubble'],
      // hierarchicalGroup: ['treemap', 'sunburst'],
      lineGroup: ['line'],
      // specializedGroup: ['waterfall', 'heatmap'],
      areaGroup: ['area', 'stackedArea', 'normalizedArea'],
      polarGroup: ['radarLine', 'radarArea', 'nightingale', 'radialColumn', 'radialBar'],
      statisticalGroup: isSimpleChart ? [] : ['histogram'], // ['boxPlot', 'rangeBar', 'rangeArea', 'histogram'],
      combinationGroup: ['columnLineCombo', 'areaColumnCombo', 'customCombo'],
    },
  },
  dataPanel: {
    groups: [
      { type: 'seriesChartType', isOpen: true },
      { type: 'categories', isOpen: true },
      { type: 'series', isOpen: true },
    ],
  },
  formatPanel: {
    groups: [
      { type: 'chart', isOpen: false },
      { type: 'titles', isOpen: false },
      { type: 'legend', isOpen: false },
      { type: 'horizontalAxis', isOpen: false },
      { type: 'verticalAxis', isOpen: false },
      { type: 'series', isOpen: false },
    ],
  },
});

/* We hide the option of selecting the chart header for charts like box-plot, so that we properly render the data
 range */
export const getStatisticalGraphType = (chartType: ChartType) => {
  return ['histogram', 'rangeBar', 'boxPlot'].includes(chartType) ? 'excluded' : 'category';
};
